// Frontend metadata for the approve-flow approval reasons.
//
// The reasons themselves are typed in `@/api/types` (`ApprovalReason`,
// extracted from the FastAPI Literal via the generated schema). This module
// ships display labels + one-line tooltips per reason, the canonical order,
// the prefill heuristic, and the reason bulk approvals carry — so every UI
// surface speaks the same vocabulary.
//
// Adding/changing a reason: edit the Pydantic Literal in
// `server_py/engine/approval_reasons.py`, run `npm run gen:api`, then update
// `APPROVAL_REASON_META` here.

import type { InvoiceLinkOut } from '@/api/invoices';
import type { ApprovalReason } from '@/api/types';

interface ApprovalReasonMeta {
  label: string;
  tooltip: string;
}

/** Stable display order — strongest confidence first. */
export const APPROVAL_REASONS_ORDERED: readonly ApprovalReason[] = [
  'invoice_confirmed',
  'supplier_sampled',
  'practitioner_judgment',
];

export const APPROVAL_REASON_META: Record<ApprovalReason, ApprovalReasonMeta> = {
  invoice_confirmed: {
    label: 'Invoice confirmed',
    tooltip: "The invoice for this transaction was reviewed and the GST/HST is on the paper.",
  },
  supplier_sampled: {
    label: 'Supplier sampled',
    tooltip:
      "A sample of this supplier's invoices was reviewed; this one is approved by extrapolation.",
  },
  practitioner_judgment: {
    label: 'Practitioner judgment',
    tooltip: 'Approved on expert reading (statute, memo, ledger data) without a this-txn invoice.',
  },
};

/** Reason stamped on a bulk approval. Approving many findings at once, without
 *  opening each invoice, is exactly the sampled/extrapolated case — so bulk
 *  approve records `supplier_sampled`. A reviewer who verified the invoice for
 *  a specific finding approves it singly to get `invoice_confirmed`. */
export const BULK_APPROVAL_REASON: ApprovalReason = 'supplier_sampled';

/** The basis a bulk approval records for one finding. Preserve an existing
 *  basis so re-approving an already `invoice_confirmed` finding in a bulk pass
 *  never downgrades it to the estimated tier (which would move dollars from the
 *  customer's confirmed total to estimated). A finding with no basis yet
 *  defaults to the bulk basis. */
export function bulkApprovalReason(existing: ApprovalReason | null | undefined): ApprovalReason {
  return existing ?? BULK_APPROVAL_REASON;
}

/** Coerce a persisted/untrusted value to a valid approval-basis filter: a known
 *  reason, or '' (any). A stale sessionStorage value would otherwise leave the
 *  select on an option that matches no finding, hiding every row. */
export function normalizeApprovalReasonFilter(value: unknown): '' | ApprovalReason {
  return typeof value === 'string' && (APPROVAL_REASONS_ORDERED as readonly string[]).includes(value)
    ? (value as ApprovalReason)
    : '';
}

/** Prefill for the single-approve picker: if any attached invoice for the
 *  transaction shows tax (a positive `tax_shown`, or `tax_separately_stated`),
 *  default to `invoice_confirmed`. Otherwise return undefined so the reviewer
 *  makes a conscious pick. A bare attachment with no captured tax is NOT a
 *  confirmation — that's the reject-shows-no-tax path, not approve. */
export function defaultApprovalReason(
  links: readonly InvoiceLinkOut[] | undefined,
): ApprovalReason | undefined {
  if (!links || links.length === 0) return undefined;
  const showsTax = links.some((l) => {
    if (l.tax_separately_stated === true) return true;
    const shown = l.tax_shown != null ? Number(l.tax_shown) : 0;
    return Number.isFinite(shown) && shown > 0;
  });
  return showsTax ? 'invoice_confirmed' : undefined;
}

/** Resolve the picker value when a prefill arrives (invoice links load after
 *  the panel mounts, so `defaultApprovalReason` can turn from undefined into
 *  `invoice_confirmed` a tick later). Apply the incoming default ONLY while the
 *  reviewer hasn't chosen yet (`current` is null) — a deliberate pick is never
 *  overwritten. */
export function applyPrefill(
  current: ApprovalReason | null,
  incoming: ApprovalReason | undefined,
): ApprovalReason | null {
  return current === null && incoming ? incoming : current;
}
